import React from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  BarController,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title,
  PointElement,
  LineElement,
  LineController
} from "chart.js";

ChartJS.register(
  BarElement,
  BarController,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title,
  PointElement,
  LineElement,
  LineController
);

// Professional target line plugin with enhanced styling
const professionalTargetLinePlugin = {
  id: 'professionalTargetLines',
  afterDatasetsDraw(chart) {
    const {
      ctx,
      data,
      chartArea: { top, bottom, left, right },
      scales: { x, y },
    } = chart;

    const barWidth = (right - left) / data.labels.length;
    const targetValues = data.datasets[0].targetValues;

    if (!targetValues) return;

    targetValues.forEach((target, index) => {
      if (target === null || target === undefined) return;
      
      const yPos = y.getPixelForValue(target);
      const barCenterX = left + (index * barWidth) + (barWidth * 0.5);
      const lineStart = left + (index * barWidth) + (barWidth * 0.15);
      const lineEnd = left + ((index + 1) * barWidth) - (barWidth * 0.15);

      ctx.save();
      
      // Create gradient for target line
      const gradient = ctx.createLinearGradient(lineStart, yPos, lineEnd, yPos);
      gradient.addColorStop(0, 'rgba(220, 38, 127, 0.8)');
      gradient.addColorStop(0.5, 'rgba(220, 38, 127, 1)');
      gradient.addColorStop(1, 'rgba(220, 38, 127, 0.8)');
      
      // Draw main target line with gradient
      ctx.beginPath();
      ctx.moveTo(lineStart, yPos);
      ctx.lineTo(lineEnd, yPos);
      ctx.strokeStyle = gradient;
      ctx.lineWidth = 3;
      ctx.stroke();
      
      // Add subtle drop shadow
      ctx.shadowColor = 'rgba(220, 38, 127, 0.3)';
      ctx.shadowBlur = 4;
      ctx.shadowOffsetY = 2;
      ctx.stroke();
      
      // Reset shadow
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;
      
      // Draw professional target indicators (diamonds)
      const diamondSize = 6;
      [lineStart, lineEnd].forEach(xPos => {
        ctx.beginPath();
        ctx.moveTo(xPos, yPos - diamondSize);
        ctx.lineTo(xPos + diamondSize, yPos);
        ctx.lineTo(xPos, yPos + diamondSize);
        ctx.lineTo(xPos - diamondSize, yPos);
        ctx.closePath();
        ctx.fillStyle = '#dc267f';
        ctx.fill();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.stroke();
      });
      
      // Professional target value label with background
      const targetText = target.toLocaleString(undefined, { 
        minimumFractionDigits: 0, 
        maximumFractionDigits: 1 
      });
      
      ctx.font = 'bold 11px "Segoe UI", Tahoma, Geneva, Verdana, sans-serif';
      const textWidth = ctx.measureText(targetText).width;
      const labelX = barCenterX;
      const labelY = yPos - 15;
      
      // Background for label
      ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.fillRect(labelX - textWidth/2 - 6, labelY - 14, textWidth + 12, 18);
      ctx.strokeStyle = '#dc267f';
      ctx.lineWidth = 1;
      ctx.strokeRect(labelX - textWidth/2 - 6, labelY - 14, textWidth + 12, 18);
      
      // Label text
      ctx.fillStyle = '#dc267f';
      ctx.textAlign = 'center';
      ctx.fillText(targetText, labelX, labelY - 2);
      
      ctx.restore();
    });
  },
};

// Alternative approach using afterDraw if beforeDraw doesn't work
const kpiTypeBackgroundPluginAlt = {
  id: 'kpiTypeBackgroundAlt',
  afterDraw(chart, args, options) {
    const { ctx, chartArea: { left, right, top, bottom } } = chart;
    
    console.log('Alternative background plugin executing');
    console.log('Options kpiTypes:', options.kpiTypes);
    
    // Try multiple ways to get kpiTypes
    let kpiTypesArray = options.kpiTypes || 
                       options.plugins?.kpiTypeBackground?.kpiTypes ||
                       chart.config?.options?.kpiTypes;
    
    if (!kpiTypesArray || !Array.isArray(kpiTypesArray)) {
      console.log('Alt plugin: No valid kpiTypes found - trying to extract from labels');
      // Fallback: try to determine types from label names
      kpiTypesArray = chart.data.labels.map(label => {
        if (typeof label === 'string') {
          if (label.includes('Environment') || label.includes('Water') || label.includes('Waste') || label.includes('Energy') || label.includes('Emission')) {
            return 'Environment';
          } else if (label.includes('Social') || label.includes('Female') || label.includes('training')) {
            return 'Social';
          } else if (label.includes('Governance') || label.includes('data breach') || label.includes('regulatory')) {
            return 'Governance';
          }
        }
        return 'Environment'; // default
      });
    }
    
    if (!kpiTypesArray || kpiTypesArray.length === 0) {
      console.log('Alt plugin: Still no valid kpiTypes found');
      return;
    }
    
    const barCount = chart.data.labels.length;
    const barWidth = (right - left) / barCount;
    
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over'; // Draw behind existing content
    
    kpiTypesArray.forEach((type, idx) => {
      let bgColor = undefined;
      
      if (type === 'Environment') {
        bgColor = 'rgba(34, 197, 94, 0.3)'; // Green background
      } else if (type === 'Social') {
        bgColor = 'rgba(147, 197, 253, 0.3)'; // Light blue background
      } else if (type === 'Governance') {
        bgColor = 'rgba(251, 191, 36, 0.3)'; // Orange background
      }
      
      if (!bgColor) return;
      
      const xStart = left + (idx * barWidth);
      
      console.log(`Alt plugin drawing ${type} at x:${xStart}, color:${bgColor}`);
      
      ctx.fillStyle = bgColor;
      ctx.fillRect(xStart, top, barWidth, bottom - top);
    });
    
    ctx.restore();
  }
};

// FIXED: Custom plugin to color the background behind each KPI bar by type
const kpiTypeBackgroundPlugin = {
  id: 'kpiTypeBackground',
  beforeDraw(chart, args, options) {
    const { ctx, chartArea: { left, right, top, bottom } } = chart;
    
    // Debug logging
    console.log('Background plugin executing');
    console.log('Chart area:', { left, right, top, bottom });
    console.log('Options kpiTypes:', options.kpiTypes);
    console.log('Options:', options);
    
    // Try multiple ways to get kpiTypes
    let kpiTypesArray = options.kpiTypes || 
                       options.plugins?.kpiTypeBackground?.kpiTypes ||
                       chart.config?.options?.kpiTypes ||
                       chart.options?.kpiTypes;
    
    console.log('Found kpiTypesArray:', kpiTypesArray);
    
    if (!kpiTypesArray || !Array.isArray(kpiTypesArray)) {
      console.log('No valid kpiTypes found - trying to extract from labels');
      // Fallback: try to determine types from label names
      kpiTypesArray = chart.data.labels.map(label => {
        if (typeof label === 'string') {
          if (label.includes('Environment') || label.includes('Water') || label.includes('Waste') || label.includes('Energy') || label.includes('Emission')) {
            return 'Environment';
          } else if (label.includes('Social') || label.includes('Female') || label.includes('training')) {
            return 'Social';
          } else if (label.includes('Governance') || label.includes('data breach') || label.includes('regulatory')) {
            return 'Governance';
          }
        }
        return 'Environment'; // default
      });
      console.log('Extracted kpiTypes from labels:', kpiTypesArray);
    }
    
    if (!kpiTypesArray || kpiTypesArray.length === 0) {
      console.log('Still no valid kpiTypes found');
      return;
    }
    
    const barCount = chart.data.labels.length;
    const barWidth = (right - left) / barCount;
    
    console.log('Bar count:', barCount, 'Bar width:', barWidth);
    
    ctx.save();
    
    kpiTypesArray.forEach((type, idx) => {
      let bgColor = undefined;
      
      if (type === 'Environment') {
        bgColor = 'rgba(34, 197, 94, 0.04)'; // Even lighter green background
      } else if (type === 'Social') {
        bgColor = 'rgba(59, 130, 246, 0.04)'; // Even lighter blue background
      } else if (type === 'Governance') {
        bgColor = 'rgba(245, 158, 11, 0.04)'; // Even lighter orange background
      }
      
      if (!bgColor) {
        console.log('No color for type:', type);
        return;
      }
      
      const xStart = left + (idx * barWidth);
      const rectWidth = barWidth;
      const rectHeight = bottom - top;
      
      console.log(`Drawing ${type} background: x=${xStart}, y=${top}, width=${rectWidth}, height=${rectHeight}, color=${bgColor}`);
      
      // Draw background rectangle
      ctx.fillStyle = bgColor;
      ctx.fillRect(xStart, top, rectWidth, rectHeight);
      
      // Add a border for better definition
      ctx.strokeStyle = type === 'Environment' ? 'rgba(34, 197, 94, 0.4)' :
                        type === 'Social' ? 'rgba(59, 130, 246, 0.4)' :
                        'rgba(245, 158, 11, 0.4)';
      ctx.lineWidth = 1;
      ctx.strokeRect(xStart, top, rectWidth, rectHeight);
    });
    
    ctx.restore();
    console.log('Background plugin completed');
  }
};

// Register both plugins - try alternative if main doesn't work
ChartJS.register(professionalTargetLinePlugin, kpiTypeBackgroundPlugin, kpiTypeBackgroundPluginAlt);

const ScorecardKPIChart = ({ kpiData, useGroupedView = false, year, targetYear }) => {
  // Debug: Log kpiData and kpiTypes to verify type property
  const kpiTypes = kpiData.map(item => item.type || item.theme || '');
  console.log('ScorecardKPIChart kpiData:', kpiData);
  console.log('ScorecardKPIChart kpiTypes:', kpiTypes);
  console.log('useGroupedView:', useGroupedView);

  const labels = kpiData.map(item => item.name);
  const currentValues = kpiData.map(item => item.current);
  const targetValues = kpiData.map(item => item.target);

  // Professional color palette - single consistent blue color
  const getBarColor = () => {
    return 'rgba(59, 130, 246, 0.8)'; // Professional blue
  };

  const getBorderColor = () => {
    return '#2563eb'; // Darker blue for border
  };

  const data = {
    labels,
    datasets: [
      {
        label: "Current Performance",
        data: currentValues,
        backgroundColor: currentValues.map(() => getBarColor()),
        borderColor: currentValues.map(() => getBorderColor()),
        borderWidth: 2,
        borderRadius: 6,
        borderSkipped: false,
        targetValues,
        // Add professional hover effects
        hoverBackgroundColor: currentValues.map(() => 'rgba(59, 130, 246, 0.95)'),
        hoverBorderWidth: 3,
      },
    ],
    // Remove kpiTypes from here - we'll pass it through options instead
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    // Pass kpiTypes through options so plugins can access them
    kpiTypes: kpiTypes,
    plugins: {
      kpiTypeBackground: {
        kpiTypes: kpiTypes // Also try passing it this way
      },
      title: {
        display: true,
        text: `KPI Performance Dashboard ${useGroupedView ? '(Grouped View)' : '(Detailed View)'} | Current Year: ${year || ''} | Target Year: ${targetYear || ''}`,
        font: { 
          size: 18, 
          weight: 'bold',
          family: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif'
        },
        color: '#1f2937',
        padding: {
          bottom: 15
        }
      },
      legend: {
        display: true,
        position: 'top',
        align: 'end',
        labels: {
          generateLabels: (chart) => [
            {
              text: 'Current Performance',
              fillStyle: 'rgba(59, 130, 246, 0.8)',
              strokeStyle: '#2563eb',
              lineWidth: 2,
              pointStyle: 'rect',
              font: {
                family: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
                size: 12
              }
            },
            {
              text: 'Target Threshold',
              fillStyle: '#dc267f',
              strokeStyle: '#dc267f',
              lineWidth: 3,
              pointStyle: 'line',
              font: {
                family: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
                size: 12
              }
            },
            {
              text: 'Environment',
              fillStyle: 'rgba(34, 197, 94, 0.15)',
              strokeStyle: 'rgba(34, 197, 94, 0.4)',
              lineWidth: 2,
              pointStyle: 'rect',
              font: {
                family: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
                size: 10
              }
            },
            {
              text: 'Social',
              fillStyle: 'rgba(59, 130, 246, 0.15)',
              strokeStyle: 'rgba(59, 130, 246, 0.4)',
              lineWidth: 2,
              pointStyle: 'rect',
              font: {
                family: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
                size: 10
              }
            },
            {
              text: 'Governance',
              fillStyle: 'rgba(245, 158, 11, 0.15)',
              strokeStyle: 'rgba(245, 158, 11, 0.4)',
              lineWidth: 2,
              pointStyle: 'rect',
              font: {
                family: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
                size: 10
              }
            }
          ],
          padding: 20,
          usePointStyle: true,
          color: '#374151'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        titleColor: '#111827',
        bodyColor: '#374151',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        displayColors: false,
        titleFont: {
          size: 13,
          weight: 'bold',
          family: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif'
        },
        bodyFont: {
          size: 12,
          family: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif'
        },
        callbacks: {
          title: function(context) {
            // Always show the full KPI name, even if long
            return context[0].label;
          },
          label: function (context) {
            const target = targetValues[context.dataIndex];
            const current = context.parsed.y;
            const type = kpiTypes[context.dataIndex];
            
            return [
              `Type: ${type}`,
              `Current Value: ${current.toLocaleString()}`,
              `Target Value: ${target.toLocaleString()}`
            ];
          },
        },
        external: function(context) {
          // Add custom shadow to tooltip
          const tooltip = context.tooltip;
          if (tooltip.opacity === 0) return;
          
          const canvas = context.chart.canvas;
          canvas.style.filter = 'drop-shadow(0 10px 25px rgba(0, 0, 0, 0.1))';
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: { 
          display: true, 
          text: "Performance Value",
          font: { 
            size: 14, 
            weight: 'bold',
            family: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif'
          },
          color: '#374151'
        },
        grid: {
          color: 'rgba(156, 163, 175, 0.3)',
          lineWidth: 1
        },
        ticks: {
          font: {
            size: 11,
            family: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif'
          },
          color: '#6b7280',
          callback: function(value) {
            return value.toLocaleString();
          }
        }
      },
      x: {
        title: {
          display: true,
          text: `Key Performance Indicators (Current: ${year || ''}, Target: ${targetYear || ''})`,
          font: { 
            size: 14, 
            weight: 'bold',
            family: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif'
          },
          color: '#374151'
        },
        ticks: {
          callback: (val, idx) => {
            const label = labels[idx];
            // Truncate long labels and add ellipsis for a cleaner look
            if (label.length > 16) {
              return label.slice(0, 16) + '...';
            }
            return label;
          },
          maxRotation: 60,
          minRotation: 60,
          font: { 
            size: 9,
            family: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif'
          },
          color: '#6b7280',
          autoSkip: false
        },
        grid: {
          display: false
        }
      },
    },
    animation: {
      duration: 1500,
      easing: 'easeInOutQuart'
    },
    interaction: {
      intersect: false,
      mode: 'index'
    }
  };

  return (
    <div style={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      padding: '15px',
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      border: '1px solid #f3f4f6',
      width: '100%', // Always fit parent
      maxWidth: '100%'
    }}>
      <div style={{
        width: '100%',
        overflowX: 'auto',
      }}>
        <div style={{
          flex: 1,
          minHeight: '350px',
          minWidth: labels.length > 8 ? labels.length * 90 : 'unset',
          width: '100%'
        }}>
          <Bar data={data} options={options} />
        </div>
      </div>
    </div>
  );
};

export default ScorecardKPIChart;